'use client';

import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import type { DOMAttributes, ReactNode } from 'react';

import type { ResponsiveOption } from '../../../types/responsiveOption';

import { focusRingStyles } from '../../../styles/focusRing';
import { type Component, type ComponentProps } from '../../../types/component';
import { mergeProps } from '../../../utils/mergeProps';
import { resolveResponsiveProps } from '../../../utils/resolveResponsiveProps';
import { tv } from '../../../utils/tv';
import { filterProps } from '../../utils/filterProps';

type FormFieldSize = 'md' | 'lg';

const formFieldClasses = {
  root: 'zds-form-field-root',
} as const;

const focusStyles = focusRingStyles();

const formFieldStyles = tv({
  base: [
    formFieldClasses.root,
    focusStyles.root(),
    'flex items-center gap-2',
    'z--form-field-radius z--form-field-border-width border-solid z--form-field-border-color surface-neutral-layer-3 hover:surface-neutral-layer-2',
    // Focused outline: uses outline instead of border to avoid layout shift on Windows. Excluded when
    // focus-visible so the focus ring wins on keyboard focus (exclusion selector has higher specificity
    // than the unconditional focus ring utilities).
    '[&[data-focused]:not([data-focus-visible])]:z--form-field-active-border-width',
    '[&[data-focused]:not([data-focus-visible])]:outline-solid',
    '[&[data-focused]:not([data-focus-visible])]:z--form-field-active-border-color',
    '[&[data-focused]:not([data-focus-visible])]:-outline-offset-[var(--z--form-field-active-border-width)]',
  ],
  variants: {
    size: {
      md: 'z--form-field-md-height z--form-field-md-padding-start z--form-field-md-padding-end',
      lg: 'z--form-field-lg-height z--form-field-lg-padding-start z--form-field-lg-padding-end',
    },
    sizeXs: {
      md: 'xs:z--form-field-md-height xs:z--form-field-md-padding-start xs:z--form-field-md-padding-end',
      lg: 'xs:z--form-field-lg-height xs:z--form-field-lg-padding-start xs:z--form-field-lg-padding-end',
    },
    sizeSm: {
      md: 'sm:z--form-field-md-height sm:z--form-field-md-padding-start sm:z--form-field-md-padding-end',
      lg: 'sm:z--form-field-lg-height sm:z--form-field-lg-padding-start sm:z--form-field-lg-padding-end',
    },
    sizeMd: {
      md: 'md:z--form-field-md-height md:z--form-field-md-padding-start md:z--form-field-md-padding-end',
      lg: 'md:z--form-field-lg-height md:z--form-field-lg-padding-start md:z--form-field-lg-padding-end',
    },
    sizeLg: {
      md: 'lg:z--form-field-md-height lg:z--form-field-md-padding-start lg:z--form-field-md-padding-end',
      lg: 'lg:z--form-field-lg-height lg:z--form-field-lg-padding-start lg:z--form-field-lg-padding-end',
    },
    sizeXl: {
      md: 'xl:z--form-field-md-height xl:z--form-field-md-padding-start xl:z--form-field-md-padding-end',
      lg: 'xl:z--form-field-lg-height xl:z--form-field-lg-padding-start xl:z--form-field-lg-padding-end',
    },
    isInvalid: {
      true: 'border-error-main',
    },
    isDisabled: {
      true: 'cursor-not-allowed disabled-border disabled-background disabled-foreground [&_*]:cursor-not-allowed',
    },
  },
});

interface FormFieldBaseProps {
  /** The height of the field box. Supports responsive breakpoints. @default 'md' */
  size?: ResponsiveOption<FormFieldSize>;
  /** Whether the field is disabled. */
  isDisabled?: boolean;
  /** Whether the field is in an invalid/error state. */
  isInvalid?: boolean;
  /**
   * Render function that receives focus props to spread on the focusable child element.
   * FormField owns focus detection internally — the consumer only spreads the props.
   */
  render: (focusProps: DOMAttributes<Element>) => ReactNode;
}

type FormFieldProps = ComponentProps<FormFieldBaseProps, 'div'>;

type FormFieldComponent = Component<FormFieldBaseProps, 'div'>;

const FormField: FormFieldComponent = (props) => {
  const [localProps, hoverRaProps, focusRingRaProps] = filterProps(props, [
    'useHover',
    'useFocusRing',
  ]);

  const {
    size: sizeProp = 'md',
    isInvalid,
    render,
    className,
    ref,
    ...restLocalProps
  } = localProps;

  const { isDisabled } = props;

  const { defaultOption: size, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);

  const { hoverProps, isHovered } = useHover(hoverRaProps);
  const { focusProps, isFocused, isFocusVisible } =
    useFocusRing(focusRingRaProps);

  return (
    <div
      {...mergeProps(restLocalProps, hoverProps)}
      ref={ref}
      data-hovered={isHovered || undefined}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      className={formFieldStyles({
        size,
        sizeXs: responsiveSize?.xs,
        sizeSm: responsiveSize?.sm,
        sizeMd: responsiveSize?.md,
        sizeLg: responsiveSize?.lg,
        sizeXl: responsiveSize?.xl,
        isDisabled,
        isInvalid,
        className,
      })}
    >
      {render(focusProps)}
    </div>
  );
};

FormField.displayName = 'FormField';

export {
  FormField,
  type FormFieldProps,
  type FormFieldBaseProps,
  formFieldClasses,
};
